// src/services/api/timeEntry.js
// API methods for time tracking (clock in/out)

/**
 * @returns {Object} Time entry methods (bound to `this` of the composed apiService)
 */
export function createTimeEntryMethods() {
  return {
    /**
     * Clock in — creates an active time entry
     * @param {number} [locationId]
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryResponse>}
     */
    clockIn(locationId) {
      const body = {};
      if (locationId) body.location_id = locationId;
      return this.request('/time-entry-clock-in', { method: 'POST', body: JSON.stringify(body) });
    },

    /**
     * Clock out — closes the active time entry
     * @param {string} [notes]
     * @param {number} [miles]
     * @param {import('@prism-ehr/shared/types/models/timeEntry').HourAllocation[]} [hourAllocations]
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryResponse>}
     */
    clockOut(notes, miles, hourAllocations) {
      const body = {};
      if (notes) body.notes = notes;
      if (miles && miles > 0) body.miles = miles;
      if (hourAllocations?.length > 0) body.hour_allocations = hourAllocations;
      return this.request('/time-entry-clock-out', { method: 'POST', body: JSON.stringify(body) });
    },

    /**
     * Get current active time entry for the authenticated user
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryResponse | null>}
     */
    getCurrentTimeEntry() {
      return this.request('/time-entry-current', { method: 'GET' });
    },

    /**
     * List time entries with filters
     * @param {Object} [filters]
     * @param {string} [filters.user_id]
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @param {string} [filters.status]
     * @param {string} [filters.location_id]
     * @param {number} [filters.page]
     * @param {number} [filters.per_page]
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryListResponse>}
     */
    getTimeEntries(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => {
        if (v != null && v !== '') params.set(k, String(v));
      });
      const qs = params.toString();
      return this.request(`/time-entry${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Admin edit a time entry
     * @param {number} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryResponse>}
     */
    updateTimeEntry(id, data) {
      return this.request(`/time-entry/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
    },

    /**
     * Admin create a completed time entry for any user
     * @param {Object} data
     * @param {string} data.user_id
     * @param {number} data.clock_in - epoch ms
     * @param {number} data.clock_out - epoch ms
     * @param {string} [data.notes]
     * @param {number} [data.miles]
     * @param {import('@prism-ehr/shared/types/models/timeEntry').HourAllocation[]} [data.hour_allocations]
     * @param {string} [data.location_id]
     * @returns {Promise<import('@prism-ehr/shared/types/api/timeEntry').TimeEntryResponse>}
     */
    adminCreateTimeEntry(data) {
      return this.request('/time-entry-admin-create', { method: 'POST', body: JSON.stringify(data) });
    },
  };
}
